import React from "react";
import { Quote } from "lucide-react";

// A reusable testimonial figure — the same visual language used in the
// Xaluca Originals carousel, extracted so both card layouts on the
// homepage (2-col flagship grid + secondary carousel) can share a
// single implementation. Localised (fr / en / es), CMS-scopable, and
// theme-aware ("light" reads on cream backgrounds, "dark" reads on the
// black/immersive Originals panel).
const pick = (obj, lang) =>
  typeof obj === "string" ? obj : obj?.[lang] || obj?.en || "";

const ExperienceTestimonial = ({
  testimonial,
  lang,
  variant = "light",
  testId,
  cmsScope,
}) => {
  if (!testimonial) return null;
  const isDark = variant === "dark";
  const originText = pick(testimonial.origin, lang);

  return (
    <figure
      className={`mt-6 pt-5 border-t ${
        isDark ? "border-[#FDFBF7]/12" : "border-[#2C2621]/10"
      }`}
      data-testid={testId}
      data-cms-scope={cmsScope}
    >
      <Quote
        className={`w-4 h-4 ${isDark ? "text-[#D4A373]" : "text-[#C16542]"} mb-2`}
        strokeWidth={1.5}
        aria-hidden="true"
      />
      <blockquote
        className={`font-serif-x italic text-[13px] md:text-sm leading-relaxed ${
          isDark ? "text-[#FDFBF7]/85" : "text-[#2C2621]/90"
        }`}
        data-cms-role="quote"
      >
        &ldquo;{pick(testimonial.quote, lang)}&rdquo;
      </blockquote>
      <figcaption className="mt-3 flex items-center gap-2.5">
        {testimonial.avatar ? (
          <img
            src={testimonial.avatar}
            alt=""
            loading="lazy"
            decoding="async"
            className={`w-7 h-7 rounded-full object-cover ring-[1.5px] ${
              isDark ? "ring-[#D4A373]/60" : "ring-[#C16542]/40"
            } shrink-0`}
            data-cms-role="avatar"
          />
        ) : null}
        <div className="min-w-0">
          <p
            className={`text-[11px] tracking-[0.18em] uppercase leading-tight ${
              isDark ? "text-[#FDFBF7]" : "text-[#2C2621]"
            } truncate`}
            data-cms-role="author"
          >
            {testimonial.author}
          </p>
          {originText ? (
            <p
              className={`text-[10px] tracking-[0.2em] uppercase leading-tight mt-0.5 ${
                isDark ? "text-[#D4A373]/80" : "text-[#5C5248]"
              } truncate`}
              data-cms-role="origin"
            >
              {originText}
            </p>
          ) : null}
        </div>
      </figcaption>
    </figure>
  );
};

// Flagship experiences (rendered in the 2-column grid at the top of
// the homepage Experiences section) — one testimonial per `key` in
// `EXPERIENCES` where `secondary !== true`.
export const FLAGSHIP_TESTIMONIALS = {
  desert: {
    quote: {
      en: "The Sahara at dawn on a camel — silent, endless, life-changing. Xaluca made every detail feel effortless.",
      fr: "Le Sahara à l'aube à dos de chameau — silencieux, infini, bouleversant. Xaluca a rendu chaque détail simple.",
      es: "El Sáhara al amanecer a lomos de camello — silencioso, infinito, transformador. Xaluca hizo que cada detalle fuera sencillo.",
    },
    author: "Sarah B.",
    origin: { en: "London", fr: "Londres", es: "Londres" },
    avatar:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=96&h=96&q=80",
  },
  atlas: {
    quote: {
      en: "Two days trekking with Amazigh guides through the M'Goun. Cedar forests, warm bread, silence. Deeply human.",
      fr: "Deux jours de randonnée avec des guides amazighs à travers le M'Goun. Cèdres, pain chaud, silence. Profondément humain.",
      es: "Dos días de trekking con guías amazigh por el M'Goun. Cedros, pan caliente, silencio. Profundamente humano.",
    },
    author: "Andrea L.",
    origin: { en: "Milan", fr: "Milan", es: "Milán" },
    avatar:
      "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=96&h=96&q=80",
  },
  kasbah: {
    quote: {
      en: "Aït Ben Haddou at golden hour, then a private dinner inside the ksar. Cinema you can taste.",
      fr: "Aït Ben Haddou à l'heure dorée, puis un dîner privé dans le ksar. Du cinéma qui se goûte.",
      es: "Aït Ben Haddou en la hora dorada, luego una cena privada dentro del ksar. Cine que se saborea.",
    },
    author: "David C.",
    origin: { en: "New York", fr: "New York", es: "Nueva York" },
    avatar:
      "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=96&h=96&q=80",
  },
  berber: {
    quote: {
      en: "We cooked tagine with the family, learned three Amazigh words, and left as friends. Rare, unforgettable.",
      fr: "Nous avons préparé un tagine en famille, appris trois mots amazighs, et sommes repartis en amis. Rare, inoubliable.",
      es: "Cocinamos tagine con la familia, aprendimos tres palabras amazigh, y nos fuimos como amigos. Raro, inolvidable.",
    },
    author: "Nadia F.",
    origin: { en: "Amsterdam", fr: "Amsterdam", es: "Ámsterdam" },
    avatar:
      "https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?auto=format&fit=crop&w=96&h=96&q=80",
  },
};

export default ExperienceTestimonial;
